/**
 * UserDetail — shadcn Dialog that renders a user's profile across four
 * tabs (v1.4 H2, issue: "Добавь возможность открыть инфу по юзеру по типу
 * как в Engagement").
 *
 * Tabs:
 *   1. Overview       — headline cards (requests, avg rating, last active,
 *                       role, first seen)
 *   2. Activity       — daily time-series bar chart of the user's requests
 *   3. Feedback       — list of rated traces with text feedback
 *   4. Tools/topics   — compact breakdown of tools the user called most
 *
 * Data comes from `useUserDetail(userId)`, which itself composes the
 * existing `/salescoach/api/user/:username/*` endpoints (see `use-user-detail.ts`).
 * Every section renders a graceful empty/loading state so partial data
 * still produces a usable modal — this is a v1 feature, perfect
 * completeness isn't required.
 *
 * Open/close state is URL-backed via `useOpenUserDetail()`. The modal is
 * mounted once at `App.tsx` top level so anywhere in the SPA can call
 * `openUserDetail(userId)` and have it surface here.
 */

import * as React from 'react';
import {
  BarChart,
  Bar,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TraceTable, type TraceColumnKey, type TraceTableSort } from '@/components/TraceTable';
import { useChartTooltipTrigger } from '@/components/charts/ChartTooltip';
import { chartPalette } from '@/lib/chart-palette';
import { tracelistRowToTableRow } from '@/lib/trace-row';
import { useOpenUserDetail } from '@/hooks/use-open-user-detail';
import { useTracesList } from '@/hooks/use-traces-list';
import { useUrlFilters } from '@/hooks/use-url-filters';
import { useUserDetail } from '@/hooks/use-user-detail';
import { UserNotFoundError } from '@/hooks/use-user-drilldown';
import {
  formatDateMedium,
  formatDateShort,
  formatRelativeCet,
} from '@/lib/format-date';
import type { UserProfile, UserToolRow } from '@/services/api-types';

// ---------------------------------------------------------------------------
// Formatters (kept local — copying tiny helpers is cheaper than importing
// across the component boundary, matches UserProfileHeader convention).
// ---------------------------------------------------------------------------

function relativeTime(iso: string | null): string {
  if (!iso) return 'never';
  return formatRelativeCet(iso);
}

function shortDate(iso: string | null): string {
  if (!iso) return '—';
  return formatDateMedium(iso);
}

function formatBucketLabel(bucket: string): string {
  return formatDateShort(bucket);
}

const NUM_FMT = new Intl.NumberFormat('en-US');

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------

function RoleBadge({ isRm }: { isRm: UserProfile['isRm'] }): React.ReactElement {
  if (isRm === true) {
    return (
      <Badge className="bg-green-600 text-white hover:bg-green-600/80">RM</Badge>
    );
  }
  if (isRm === false) {
    return (
      <Badge variant="secondary" className="bg-blue-100 text-blue-900">
        Non-RM
      </Badge>
    );
  }
  return <Badge variant="outline">Unknown</Badge>;
}

function StatCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
}): React.ReactElement {
  return (
    <Card>
      <CardContent className="p-3">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="mt-1 text-lg font-semibold tabular-nums">{value}</div>
        {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
      </CardContent>
    </Card>
  );
}

function EmptyState({ message }: { message: string }): React.ReactElement {
  return (
    <div
      data-testid="user-detail-empty"
      className="rounded border border-dashed border-border p-6 text-sm text-muted-foreground text-center"
    >
      {message}
    </div>
  );
}

function SkeletonBlock({ height = 80 }: { height?: number }): React.ReactElement {
  return (
    <div
      className="w-full rounded bg-muted animate-pulse"
      style={{ height }}
      data-testid="user-detail-skeleton"
    />
  );
}

// ---------------------------------------------------------------------------
// Feedback tab body — Phase 8 Plan 04 migration. Replaces the deleted
// per-user feedback-timeline hook + FeedbackCard list with `<TraceTable>`
// driven by `useTracesList(...,{hasFeedback:'rated'})`. No expand needed —
// the modal already shows the row in the parent context.
// ---------------------------------------------------------------------------

const FEEDBACK_COLUMNS: TraceColumnKey[] = [
  'time',
  'rating',
  'queryAnswer',
  'feedback',
];

const FEEDBACK_SORT: TraceTableSort = { key: 'timestamp', dir: 'desc' };

function FeedbackTabBody({
  feedback,
  fromIso,
  toIso,
}: {
  feedback: ReturnType<typeof useTracesList>;
  fromIso: string | undefined;
  toIso: string | undefined;
}): React.ReactElement {
  const rawRows = feedback.data?.rows ?? [];
  const rows = React.useMemo(
    () => rawRows.map(tracelistRowToTableRow),
    [rawRows],
  );

  // The modal table is non-interactive on sort — `<TraceTable>` requires the
  // prop wired but the sort key never changes here.
  const noopSort = React.useCallback(() => {}, []);

  return (
    <TraceTable
      rows={rows}
      columns={FEEDBACK_COLUMNS}
      sort={FEEDBACK_SORT}
      onSort={noopSort}
      from={fromIso}
      to={toIso}
      isLoading={feedback.isLoading}
      isError={feedback.isError}
      onRetry={() => void feedback.refetch()}
      emptyMessage="No rated traces in the selected window."
      testIdPrefix="user-detail-feedback"
      expandable={false}
    />
  );
}

// ---------------------------------------------------------------------------
// Body — only mounted when `userId` is set so no query fires while closed
// ---------------------------------------------------------------------------

function UserDetailBody({ userId }: { userId: string }): React.ReactElement {
  const { profile, kpis, activity, tools } = useUserDetail(userId);
  const { filters } = useUrlFilters();
  // Phase 8 Plan 04: Feedback tab now hydrates from
  // `/salescoach/api/traces/list` so the modal speaks the same backend
  // surface as UserDrilldown's Activity tab. `hasFeedback:'rated'` keeps
  // the panel restricted to rated traces — same intent as the deleted
  // per-user feedback-timeline hook.
  // `admins: 'included'` (2026-08-19) — the sibling of the same line in
  // UserDrilldownPage, and for the same reason: this modal is one named
  // person's record, its server-side panels ignore the global Admins filter
  // (`USER_DRILLDOWN_FILTERS`, services/analytics/queries.ts), and a
  // `users:[…]` scope has already chosen the population this filter chooses.
  // Without it an admin's modal would show his KPIs and tools above an empty
  // Feedback panel. It can only change what this request returns when
  // `userId` IS an admin.
  const feedback = useTracesList(
    { ...filters, admins: 'included', users: [userId] },
    { hasFeedback: 'rated', limit: 10, sort: 'newest' },
    /* enabled */ true,
  );
  // Mobile tap persistence — see ChartTooltip.useChartTooltipTrigger.
  const tooltipTrigger = useChartTooltipTrigger();

  // 404 — render a friendly "not found" state inside the modal rather than
  // throwing. The parent already has an Engagement link in context.
  if (profile.isError && profile.error instanceof UserNotFoundError) {
    return (
      <div className="space-y-3 py-4">
        <p className="text-sm text-muted-foreground">
          No traces on record for{' '}
          <span className="font-mono">{profile.error.username}</span>.
        </p>
      </div>
    );
  }

  const p = profile.data;
  const k = kpis.data;
  const requestsSeries = activity.data?.requests ?? [];

  return (
    <Tabs defaultValue="overview" className="w-full">
      <TabsList className="w-full grid grid-cols-4">
        <TabsTrigger value="overview" data-testid="user-detail-tab-overview">
          Overview
        </TabsTrigger>
        <TabsTrigger value="activity" data-testid="user-detail-tab-activity">
          Activity
        </TabsTrigger>
        <TabsTrigger value="feedback" data-testid="user-detail-tab-feedback">
          Feedback
        </TabsTrigger>
        <TabsTrigger value="tools" data-testid="user-detail-tab-tools">
          Tools
        </TabsTrigger>
      </TabsList>

      {/* --- Overview tab: headline cards --- */}
      <TabsContent value="overview" className="pt-4">
        {profile.isLoading || !p ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonBlock key={i} height={68} />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-3 flex-wrap">
              <RoleBadge isRm={p.isRm} />
              {p.cohort && (
                <Badge variant="outline" data-testid="user-detail-cohort">
                  {p.cohort}
                </Badge>
              )}
              {p.slackId && (
                <span className="font-mono text-xs text-muted-foreground">
                  {p.slackId}
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <StatCard
                label="Total requests"
                value={NUM_FMT.format(p.totalRequests)}
                hint="lifetime"
              />
              <StatCard
                label="Avg rating"
                /* The em-dash is this card's "no rating in this window", so
                   it may not also mean "not counted yet" — the two are
                   different facts and the second one arrives on every filter
                   change. Same '…' the two cards below use, and the same one
                   the hint underneath already used while this line said '—'. */
                value={
                  kpis.isLoading
                    ? '…'
                    : k?.avgRating.current != null
                      ? `${k.avgRating.current.toFixed(1)}★`
                      : '—'
                }
                // Show the sample size alongside the average so "5.0★ backed
                // by 1 vote" reads differently from "5.0★ backed by 20" —
                // the hint doubles as the "количество рейтингов" the user
                // asked for, without needing its own card.
                hint={
                  kpis.isLoading
                    ? 'loading…'
                    : k?.ratingsCount.current != null
                      ? `${NUM_FMT.format(k.ratingsCount.current)} ratings · current window`
                      : 'current window'
                }
              />
              <StatCard
                label="Text feedback"
                // Free-text comments left by the user — separate from star
                // ratings. Backend aggregate already computed in
                // computeUserKpiWindow (queries.ts ~3704-3707).
                value={
                  k?.textFeedbackCount.current != null
                    ? NUM_FMT.format(k.textFeedbackCount.current)
                    : kpis.isLoading
                      ? '…'
                      : '—'
                }
                hint="current window"
              />
              <StatCard
                label="Last active"
                value={relativeTime(p.lastActive)}
                hint={p.lastActive ? shortDate(p.lastActive) : undefined}
              />
              <StatCard
                label="First seen"
                value={shortDate(p.firstActive)}
              />
              <StatCard
                label="Requests (window)"
                value={
                  k?.requests.current != null
                    ? NUM_FMT.format(k.requests.current)
                    : kpis.isLoading
                      ? '…'
                      : '—'
                }
              />
              <StatCard
                label="Voice share"
                value={`${Math.round(p.voiceShare * 100)}%`}
                hint="lifetime"
              />
            </div>
          </div>
        )}
      </TabsContent>

      {/* --- Activity tab: daily requests bar chart --- */}
      <TabsContent value="activity" className="pt-4">
        {activity.isLoading ? (
          <SkeletonBlock height={220} />
        ) : activity.isError ? (
          <EmptyState message="Failed to load activity." />
        ) : requestsSeries.length === 0 ? (
          <EmptyState message="No activity in the selected window." />
        ) : (
          <div
            data-testid="user-detail-activity-chart"
            style={{ width: '100%', height: 220 }}
          >
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={requestsSeries}
                margin={{ top: 10, right: 16, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="bucket"
                  tickFormatter={(v: string) => formatBucketLabel(v)}
                />
                <YAxis allowDecimals={false} />
                <Tooltip
                  {...tooltipTrigger}
                  labelFormatter={(v: string) => formatBucketLabel(v)}
                  formatter={(v: number) => [NUM_FMT.format(v), 'Requests']}
                />
                <Bar
                  dataKey="requests"
                  fill={chartPalette.primary}
                  radius={[3, 3, 0, 0]}
                  name="Requests"
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </TabsContent>

      {/* --- Feedback tab: rated traces (Phase 8 — TraceTable+useTracesList) --- */}
      <TabsContent value="feedback" className="pt-4">
        <FeedbackTabBody
          feedback={feedback}
          fromIso={filters.from ?? undefined}
          toIso={filters.to ?? undefined}
        />
      </TabsContent>

      {/* --- Tools tab: top tools the user called --- */}
      <TabsContent value="tools" className="pt-4">
        {tools.isLoading ? (
          <SkeletonBlock height={160} />
        ) : tools.isError ? (
          <EmptyState message="Failed to load tool usage." />
        ) : !tools.data || tools.data.length === 0 ? (
          <EmptyState message="No tool calls in the selected window." />
        ) : (
          <ToolsList rows={tools.data} />
        )}
      </TabsContent>
    </Tabs>
  );
}

/**
 * Simple horizontal bar rendering of tool usage — proportional to the top
 * row's call count. Keeps this off the Recharts budget since the list is
 * already ranked and a full chart is overkill for 10 rows.
 */
function ToolsList({ rows }: { rows: UserToolRow[] }): React.ReactElement {
  const max = Math.max(1, ...rows.map((r) => r.calls));
  return (
    <ul className="space-y-2" data-testid="user-detail-tools-list">
      {rows.map((r) => {
        const pct = Math.round((r.calls / max) * 100);
        return (
          <li
            key={r.name}
            data-testid="user-detail-tool-row"
            className="rounded border border-border p-2"
          >
            <div className="flex items-center justify-between text-sm">
              <span className="font-mono text-xs">{r.name}</span>
              <span className="tabular-nums text-muted-foreground">
                {NUM_FMT.format(r.calls)} calls
              </span>
            </div>
            <div className="mt-1 h-1.5 rounded bg-muted overflow-hidden">
              <div
                className="h-full bg-primary"
                style={{ width: `${pct}%` }}
                aria-hidden
              />
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              last used {relativeTime(r.lastUsed)}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Public entry — mounted once at App.tsx top level; reads URL state itself
// ---------------------------------------------------------------------------

export function UserDetail(): React.ReactElement {
  const { userId, closeUserDetail } = useOpenUserDetail();
  const open = userId !== null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) closeUserDetail();
      }}
    >
      <DialogContent
        data-testid="user-detail-dialog"
        className="max-w-3xl max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>
            {userId ? `@${userId}` : 'User'}
          </DialogTitle>
          <DialogDescription>
            Profile, activity, feedback, and tools for this user — honours the
            current dashboard filters.
          </DialogDescription>
        </DialogHeader>
        {userId ? <UserDetailBody userId={userId} /> : null}
      </DialogContent>
    </Dialog>
  );
}
